import type { AttendeeLearningRecordValues, AttendeeLearningStatementReference } from "@verity-connect/contracts";

export type LearningRecordCoverage = "personal" | "program" | "presenter";

export interface LearningRecordCoverageSummary {
  level: LearningRecordCoverage;
  sourceCount: number;
  label: string;
}

export interface LearningRecordSeedInput {
  body: string;
  markers: readonly { kind: "key_idea" | "question" | "next_action"; text: string }[];
}

/** Copies only what the attendee wrote; it does not add or infer a claim. */
export function seedLearningRecordValues(input: LearningRecordSeedInput) {
  const keyIdeas = input.markers.filter((marker) => marker.kind === "key_idea").map((marker) => marker.text.trim()).filter(Boolean);
  return {
    centralIdea: keyIdeas[0] ?? "",
    mainPoints: keyIdeas.slice(1),
    frameworks: [],
    examples: [],
    practicalStrategies: [],
    resources: [],
    whatStoodOut: input.body.trim(),
    questions: input.markers.filter((marker) => marker.kind === "question").map((marker) => marker.text.trim()).filter(Boolean),
    nextActions: input.markers.filter((marker) => marker.kind === "next_action").map((marker) => marker.text.trim()).filter(Boolean),
  };
}

/**
 * Coverage describes what is actually available. It never claims that a note
 * has been summarized or checked by AI.
 */
export function learningRecordCoverage(input: {
  hasPrivateNote: boolean;
  hasOfficialDescription: boolean;
  presenterResourceCount: number;
  hasRecordingTranscript?: boolean;
}): LearningRecordCoverageSummary {
  const sourceCount = Number(input.hasPrivateNote)
    + Number(input.hasOfficialDescription)
    + Number(input.presenterResourceCount > 0)
    + Number(Boolean(input.hasRecordingTranscript));

  if (input.presenterResourceCount > 0 || input.hasRecordingTranscript) {
    const names = [
      input.hasPrivateNote ? "notes" : null,
      input.hasOfficialDescription ? "program" : null,
      input.hasRecordingTranscript ? "recording" : null,
      input.presenterResourceCount > 0 ? "presenter material" : null,
    ].filter((name): name is string => name !== null);
    const joined = names.length === 1 ? `${names[0]} only`
      : names.length === 2 ? `${names[0]} and ${names[1]}`
        : `${names.slice(0, -1).join(", ")}, and ${names[names.length - 1]}`;
    const label = `${joined.charAt(0).toUpperCase()}${joined.slice(1)}`;
    return { level: "presenter", sourceCount, label };
  }
  if (input.hasOfficialDescription) {
    return { level: "program", sourceCount, label: input.hasPrivateNote ? "Notes and the official program" : "Official program only" };
  }
  return { level: "personal", sourceCount, label: input.hasPrivateNote ? "Your notes" : "No notes yet" };
}

/** Keeps model sources only while the cited statement remains unchanged. */
export function reconcileLearningStatementReferences(
  values: AttendeeLearningRecordValues,
  references: readonly AttendeeLearningStatementReference[],
): readonly AttendeeLearningStatementReference[] {
  const available = [...references];
  const result: AttendeeLearningStatementReference[] = [];
  const add = (field: keyof AttendeeLearningRecordValues, text: string, index: number) => {
    const value = text.trim();
    if (!value) return;
    const match = available.findIndex((reference) => reference.field === field && reference.text === value);
    const prior = match >= 0 ? available.splice(match, 1)[0] : undefined;
    result.push(prior ? { ...prior, index } : { field, index, text: value, sourceIds: [], origin: "attendee" });
  };
  add("centralIdea", values.centralIdea, 0);
  values.mainPoints.forEach((value, index) => add("mainPoints", value, index));
  values.frameworks.forEach((value, index) => add("frameworks", value, index));
  values.examples.forEach((value, index) => add("examples", value, index));
  values.practicalStrategies.forEach((value, index) => add("practicalStrategies", value, index));
  values.resources.forEach((value, index) => add("resources", value, index));
  add("whatStoodOut", values.whatStoodOut, 0);
  values.questions.forEach((value, index) => add("questions", value, index));
  values.nextActions.forEach((value, index) => add("nextActions", value, index));
  return result;
}
